/**
 * The headset session: one screen, one menu, and the controllers going out to the arm.
 *
 * Deliberately outside React. This runs at the headset's refresh rate and touches the same
 * objects every frame; a render pass per controller sample would be the wrong shape entirely.
 * React hands it a view it can read and callbacks it can fire, and otherwise stays out.
 */
import { Box3, BufferGeometry, CanvasTexture, Group, Line, LineBasicMaterial, Matrix4, Mesh, MeshBasicMaterial,
         PerspectiveCamera, PlaneGeometry, Raycaster, Scene, Vector2, Vector3,
         WebGLRenderer } from "three";

import type { BoxMap, ToolModel } from "../adamo/boxmap";
import type { HandFrame, Rim, Settings, Status } from "../adamo/types";
import { BoxMapView } from "../boxmap/view";
import { buzz, headQuaternion, readHand, readRay } from "./controllers";
import { buildAutomation } from "./menu/automationModel";
import { buildMenu, type View } from "./menu/menuModel";
import { PANEL_W, Panel, valueFromFraction, type Widget } from "./menu/widgets";
import type { LinkMeter } from "./meter";
import { FrameSink } from "./videoTexture";
import { StereoScreen } from "./stereoScreen";

const HAND_HZ = 90;
const PANEL_WORLD_W = 0.78;
const GEAR = 0.14;
const MENU_DISTANCE = 1.0;   // metres from the viewer, matching the screen
const TAP_MS = 220;          // shorter than this is a tap on X, longer is a roll of the gripper

export type SceneCallbacks = {
  view: () => View;
  onHands: (hands: Partial<Record<"left" | "right", HandFrame>>) => void;
  onAction: (name: string) => void;
  onSetting: <K extends keyof Settings>(key: K, value: Settings[K], final: boolean) => void;
  onLocal: (patch: Partial<{ convergence: number; swapEyes: boolean; flatten: boolean;
                             verticalOffset: number; showMap: boolean }>) => void;
  onEnd: () => void;
  /** Once a frame: whatever the arm last said, and when it arrived (performance.now()). */
  latestStatus?: () => { status: Status | null; arrivedAt: number };
  /** Once a frame: the newest box map the arm has sent, if any. */
  latestBoxMap?: () => BoxMap | null;
  latestToolModel?: () => ToolModel | null;
};

export class VrScene {
  readonly sink = new FrameSink();
  private readonly renderer: WebGLRenderer;
  private readonly scene = new Scene();
  private readonly camera = new PerspectiveCamera(70, 1, 0.05, 50);
  private readonly screen: StereoScreen;
  private readonly menuGroup = new Group();
  private readonly panel = new Panel();
  private readonly panelTexture: CanvasTexture;
  private readonly panelMesh: Mesh;
  private readonly gearMesh: Mesh;
  private readonly gearTexture: CanvasTexture;
  // The camera mount against the box: a strip over the top of the picture while it is near or
  // about to hit. The top because that is the side the mount sticks out on, and in the picture
  // rather than beside it because a warning you have to look away to read is one you miss.
  private readonly rimCanvas = document.createElement("canvas");
  private readonly rimTexture: CanvasTexture;
  private readonly rimMesh: Mesh;
  private rimText = "";
  // The automation panel: the path's buttons, just right of the picture under the gear, with the
  // controls still live (automationModel.ts). Its top edge stays put as it grows or shrinks.
  private readonly autoGroup = new Group();
  private readonly autoPanel = new Panel();
  private readonly autoTexture: CanvasTexture;
  private readonly autoMesh: Mesh;
  private automation = false;
  private autoCursor: { x: number; y: number } | null = null;
  private clearArmedUntil = 0;   // "Start over" wants a second press inside CLEAR_CONFIRM_MS
  /**
   * Who a trigger press belongs to, decided the moment it starts and kept until it lets go: a press
   * that starts on a panel or the gear is a click and never reaches the gripper, even if it slides
   * off the button; one that starts anywhere else is the gripper's, even if the ray then sweeps over
   * the automation panel -- which is in view the whole time you drive, and would otherwise let go of
   * a held trigger and pull it again (the gripper toggles on a pull: a dropped catch).
   */
  private readonly triggerOwner: Record<"left" | "right", "menu" | "arm" | null> = { left: null, right: null };
  // The box map as a model on a stand to the left of the picture (the menu is to the right),
  // tipped toward you: the arm's base nearest, the box beyond it, the mount in its colour.
  private readonly mapHolder = new Group();
  private readonly boxMap = new BoxMapView({ scale: MAP_SCALE });
  private boxMapAt = 0;
  /** How much further left, about you, the stand has been turned to keep the map off the picture. */
  private mapTurn = 0;
  private readonly mapHome = new Matrix4();   // the stand's pose before any turn
  private readonly mapBox = new Box3();
  private toolModelSet = false;
  private readonly raycaster = new Raycaster();
  private readonly rayLine: Line;
  private session: XRSession | null = null;
  private refSpace: XRReferenceSpace | null = null;
  private placed = false;
  // Shut to start with. Opening it was meant to make the menu findable, but the controls are
  // stilled while it is open, so entering VR landed you in a session where no button did
  // anything -- including the one that enables the arm. The gear on the screen's edge, which
  // lights up when pointed at, is the answer to findability; this is not.
  private open = false;
  private stereo = false;
  private lastSent = 0;
  private lastTrigger = { left: false, right: false };
  private dragging: string | null = null;
  private menuButtonSince = 0;   // when X went down on the free hand, for tap versus hold
  private cursor: { x: number; y: number } | null = null;

  private restoreVisibility: (() => void) | null = null;

  constructor(private readonly cb: SceneCallbacks, private readonly meter: LinkMeter | null = null,
              private readonly me = "") {
    this.renderer = new WebGLRenderer({ alpha: true, antialias: false, powerPreference: "high-performance" });
    this.renderer.xr.enabled = true;
    this.renderer.xr.setReferenceSpaceType("local-floor");
    this.renderer.setClearAlpha(0);
    this.screen = new StereoScreen(this.sink.texture);
    this.scene.add(this.screen.group);

    this.panelTexture = new CanvasTexture(this.panel.canvas);
    this.panelMesh = new Mesh(new PlaneGeometry(PANEL_WORLD_W, PANEL_WORLD_W),
                              new MeshBasicMaterial({ map: this.panelTexture, transparent: true, depthTest: false }));
    this.panelMesh.renderOrder = 10;
    this.panelMesh.visible = false;
    this.gearTexture = new CanvasTexture(gearCanvas());
    this.gearMesh = new Mesh(new PlaneGeometry(GEAR, GEAR),
                             new MeshBasicMaterial({ map: this.gearTexture, transparent: true, depthTest: false }));
    this.gearMesh.renderOrder = 10;
    this.panelMesh.visible = false;
    this.gearMesh.visible = true;    // one or the other, never both
    this.menuGroup.add(this.panelMesh);
    // The gear stays on the screen's own right edge rather than with the panel. Put where the
    // panel goes, it would be a quarter turn away -- so once the menu was shut there was
    // nothing in front of you to open it with.
    this.screen.group.add(this.menuGroup, this.gearMesh);

    this.rimCanvas.width = RIM_PX_W;
    this.rimCanvas.height = RIM_PX_H;
    this.rimTexture = new CanvasTexture(this.rimCanvas);
    this.rimMesh = new Mesh(new PlaneGeometry(RIM_W, RIM_W * (RIM_PX_H / RIM_PX_W)),
                            new MeshBasicMaterial({ map: this.rimTexture, transparent: true, depthTest: false }));
    this.rimMesh.renderOrder = 11;
    this.rimMesh.visible = false;
    this.screen.group.add(this.rimMesh);

    this.autoTexture = new CanvasTexture(this.autoPanel.canvas);
    this.autoMesh = new Mesh(new PlaneGeometry(AUTO_WORLD_W, AUTO_WORLD_W),
                             new MeshBasicMaterial({ map: this.autoTexture, transparent: true, depthTest: false }));
    this.autoMesh.renderOrder = 10;
    this.autoGroup.add(this.autoMesh);
    // Off the picture's right edge, a little nearer than the screen and below the gear: turned to face
    // you (one screen-distance in front of the screen) and tipped up a touch, being under eye level.
    this.autoGroup.position.set(Math.sin(AUTO_BEARING) * AUTO_DISTANCE, AUTO_TOP,
                                1 - Math.cos(AUTO_BEARING) * AUTO_DISTANCE);
    this.autoGroup.rotation.set(-0.12, -AUTO_BEARING, 0, "YXZ");   // negative x: the face tips up, to your eyes
    this.autoGroup.visible = false;
    this.screen.group.add(this.autoGroup);

    this.boxMap.group.scale.setScalar(MAP_SCALE);
    this.boxMap.group.position.copy(this.boxMap.focus).multiplyScalar(-MAP_SCALE);   // until a map says where the box is
    this.mapHolder.add(this.boxMap.group);
    this.placeMap(0);
    this.mapHolder.updateMatrix();
    this.mapHome.copy(this.mapHolder.matrix);
    this.mapHolder.visible = false;
    this.screen.group.add(this.mapHolder);

    // A line out of the controller. Without it there is no way to tell a menu that is not
    // listening from a hand that is not pointing where you think it is.
    const path = new BufferGeometry().setFromPoints([new Vector3(0, 0, 0), new Vector3(0, 0, -1)]);
    this.rayLine = new Line(path, new LineBasicMaterial({ transparent: true, opacity: 0.6 }));
    this.rayLine.visible = false;
    this.rayLine.frustumCulled = false;
    this.scene.add(this.rayLine);
  }

  get canvas(): HTMLCanvasElement { return this.renderer.domElement; }

  async start(session: XRSession, stereo: boolean): Promise<void> {
    this.session = session;
    this.stereo = stereo;
    await this.renderer.xr.setSession(session);
    this.refSpace = this.renderer.xr.getReferenceSpace();
    // The relay's decoder suspends a subscription whose page reports itself hidden, and a
    // browser that has handed its display to an immersive session reports exactly that. The
    // original page had to do this too; without it the video simply stops in the headset.
    this.restoreVisibility = forceVisible();
    session.addEventListener("end", () => this.stop());
    this.layoutMenu();
    this.renderer.setAnimationLoop((_t, frame) => this.tick(frame));
  }

  stop(): void {
    this.renderer.setAnimationLoop(null);
    this.restoreVisibility?.();
    this.restoreVisibility = null;
    this.sink.dispose();
    this.screen.dispose();
    this.panelTexture.dispose();
    this.gearTexture.dispose();
    this.rimTexture.dispose();
    this.autoTexture.dispose();
    this.boxMap.dispose();
    this.renderer.dispose();
    this.session = null;
    this.cb.onEnd();
  }

  recentre(): void { this.placed = false; }

  /** Open or shut the panel. Only one of the panel and the gear is ever there to be hit. */
  private setOpen(open: boolean): void {
    this.open = open;
    this.panelMesh.visible = open;
    this.gearMesh.visible = !open;
    this.autoGroup.visible = this.automation && !open;   // under the settings, which still the controls
    this.dragging = null;
  }

  /** Show or hide the automation panel. It never stills the controls: that is the point of it. */
  private setAutomation(on: boolean): void {
    this.automation = on;
    this.clearArmedUntil = 0;
    this.autoGroup.visible = on && !this.open;
    this.widgetsChangedAt = 0;   // drawn now, not on the next redraw: it is not drawn while hidden
  }

  /** The browser's own view preferences, applied before the session starts. */
  setView(view: { convergence?: number; swapEyes?: boolean; flatten?: boolean;
                  verticalOffset?: number }): void {
    this.screen.set(view);
  }

  /** The arm buzzing the operator: a grasp landing, a refusal, a cue that it is low enough. */
  pulseHand(side: "left" | "right", intensity: number, ms: number): void {
    for (const source of this.session?.inputSources ?? []) {
      if (source.handedness === side) buzz(source, intensity, ms);
    }
  }

  private tick(frame: XRFrame | undefined): void {
    if (!frame || !this.refSpace) return;
    const now = performance.now();
    // what the operator saw, timed here because nothing else can see it (meter.ts)
    this.meter?.xrFrame(now, this.session?.frameRate);
    if (this.sink.flush()) {
      this.screen.setSource(this.sink.width, this.sink.height, this.stereo);
      this.meter?.videoShown(this.sink.timestamp, now);
    }
    const latest = this.cb.latestStatus?.();
    if (latest) this.meter?.statusSeen(latest.status?.perf, latest.arrivedAt, this.me);
    this.showRim(latest?.status?.rim ?? null);
    this.showBoxMap(latest?.status ?? null);
    const viewer = frame.getViewerPose(this.refSpace);
    if (viewer && !this.placed) {
      const { position, orientation: q } = viewer.transform;
      const yaw = Math.atan2(2 * (q.w * q.y + q.x * q.z), 1 - 2 * (q.y * q.y + q.x * q.x));
      this.screen.place(position, yaw);
      this.positionMenu();
      this.placed = true;
    }
    this.handleInput(frame);
    this.renderer.render(this.scene, this.camera);
  }

  /** The model beside the picture: a new map when one has come, the mount and the taught path every frame. */
  private showBoxMap(status: Status | null): void {
    if (!this.cb.view().showMap) {
      this.mapHolder.visible = false;
      return;
    }
    const map = this.cb.latestBoxMap?.() ?? null;
    if (!this.toolModelSet) {
      const tool = this.cb.latestToolModel?.() ?? null;
      if (tool) { this.boxMap.setToolModel(tool); this.toolModelSet = true; }
    }
    if (map && map.receivedAt !== this.boxMapAt) {
      this.boxMapAt = map.receivedAt;
      this.boxMap.setMap(map);
      // the box's middle on the stand's centre
      this.boxMap.group.position.copy(this.boxMap.focus).multiplyScalar(-MAP_SCALE);
    }
    this.boxMap.setStatus(status);
    // A path is worth the stand on its own: it is taught before the camera has mapped much of anything.
    this.mapHolder.visible = map !== null || this.boxMap.hasPath;
    this.fitMap();
  }

  /**
   * Turn the stand further left about you, just enough that nothing on it -- the voxels, the taught
   * path -- reaches the picture's left edge; and back again if the map shrinks. It is a wide box, or
   * an END taught well off to the side, that used to disappear behind the screen.
   */
  private fitMap(): void {
    let turn = 0;
    const box = this.boxMap.extent(this.mapBox);
    if (!box.isEmpty()) {
      this.boxMap.group.updateMatrix();
      const m = MAP_M.multiplyMatrices(this.mapHome, this.boxMap.group.matrix);   // group -> screen group, unturned
      const edge = Math.atan2(this.screen.size().width / 2, VIEWER.z) + MAP_MARGIN;
      let least = Infinity;   // the smallest bearing, left of straight ahead, of any corner of what is drawn
      for (let i = 0; i < 8; i += 1) {
        CORNER.set(i & 1 ? box.max.x : box.min.x, i & 2 ? box.max.y : box.min.y, i & 4 ? box.max.z : box.min.z)
          .applyMatrix4(m);
        least = Math.min(least, Math.atan2(VIEWER.x - CORNER.x, VIEWER.z - CORNER.z));
      }
      turn = Math.min(Math.max(0, edge - least), MAP_TURN_MAX);
    }
    if (Math.abs(turn - this.mapTurn) > 1e-3) this.placeMap(turn);
  }

  /** The stand `turn` radians further left than its home, the same distance from you, still facing you. */
  private placeMap(turn: number): void {
    this.mapTurn = turn;
    this.mapHolder.position.copy(MAP_HOME).sub(VIEWER).applyAxisAngle(UP, turn).add(VIEWER);
    // turned to face you (you are one screen-distance in front of the screen) and tipped toward you
    const toYou = CORNER.copy(VIEWER).sub(this.mapHolder.position);
    this.mapHolder.rotation.set(0.45, Math.atan2(toYou.x, toYou.z), 0, "YXZ");
  }

  /** The strip over the top of the picture: drawn again only when what it says changes. */
  private showRim(rim: Rim | null | undefined): void {
    const on = !!rim && rim.level !== "clear";
    this.rimMesh.visible = on;
    if (!on || !rim) { this.rimText = ""; return; }
    const head = rim.level === "hit" ? "CAMERA WILL HIT THE BOX" : "CAMERA CLOSE TO THE BOX";
    const mm = rim.clear_mm === null ? "" : `  ·  ${Math.max(rim.clear_mm, 0)} mm`;
    const text = `${rim.level}|${head}${mm}|${rim.say}`;
    const { height } = this.screen.size();
    const stripH = (this.rimMesh.geometry as PlaneGeometry).parameters.height;
    this.rimMesh.position.set(0, height / 2 - stripH / 2 - 0.02, 0.02);
    if (text === this.rimText) return;
    this.rimText = text;
    drawRim(this.rimCanvas, rim.level === "hit", head + mm, rim.say);
    this.rimTexture.needsUpdate = true;
  }

  // ---- the menu ------------------------------------------------------------

  private layoutMenu(): void {
    const view = this.cb.view();
    layOut(this.panel, this.panelTexture, this.panelMesh, PANEL_WORLD_W, buildMenu(view),
           this.open ? this.cursor : null, this.dragging);
    if (!this.automation) return;
    if (this.clearArmedUntil && performance.now() > this.clearArmedUntil) this.clearArmedUntil = 0;
    const worldH = layOut(this.autoPanel, this.autoTexture, this.autoMesh, AUTO_WORLD_W,
                          buildAutomation(view, this.clearArmedUntil > 0), this.autoCursor, null);
    this.autoMesh.position.y = -worldH / 2;   // hangs from the group's point: the top edge stays put
  }

  private positionMenu(): void {
    // A quarter turn to the right of where you are looking, facing back at you, the same
    // distance away as the screen. Turn your head to the settings, turn back to the work --
    // rather than having them crowd the edge of the picture you are working in.
    const away = MENU_DISTANCE;
    this.menuGroup.position.set(away, 0, away);
    this.menuGroup.rotation.set(0, -Math.PI / 2, 0);
    this.panelMesh.position.set(0, 0, 0);
    // Just off the right edge of the picture, at eye level: in view the whole time you are
    // working, and pointing at it is a small movement rather than a search.
    this.gearMesh.position.set(1.5 / 2 + GEAR / 2 + 0.04, 0, 0.02);
  }

  private widgetsChangedAt = 0;

  private handleInput(frame: XRFrame): void {
    const session = this.session;
    if (!session || !this.refSpace) return;
    const hands: Partial<Record<"left" | "right", HandFrame>> = {};
    const head = headQuaternion(frame, this.refSpace);
    let hovering: { x: number; y: number } | null = null;
    let autoHovering: { x: number; y: number } | null = null;
    let overGear = false;
    // What the buttons really were, before the menu stilled them. Read this for anything the
    // menu itself listens to, or it can never be shut again with the button that opened it.
    const raw: Partial<Record<"left" | "right", boolean>> = {};

    for (const source of session.inputSources) {
      const side = source.handedness;
      if (side !== "left" && side !== "right") continue;
      const hand = readHand(source, frame, this.refSpace);
      const squeeze = hand?.trigger ?? 0;           // before the menu stills it, below
      const pulled = squeeze > 0.6;
      if (hand) raw[side] = hand.a;
      if (hand) {
        hand.head_quat = head;
        // With the panel open the controls belong to the menu, not to the arm. Only the
        // inputs are stilled -- where the hands are still goes out, so the robot can see it
        // is being held and does not decide the operator has walked away.
        if (this.open) {
          hand.trigger = 0;
          hand.squeeze = hand.stick_press = hand.a = hand.b = false;
          hand.stick = [0, 0];
        }
        hands[side] = hand;
      }
      // Either hand may point. Restricting it to one was a guess about which hand an operator
      // would use, and a wrong guess there looks exactly like a menu that does not work.
      const ray = readRay(source, frame, this.refSpace);
      const hit = ray ? this.pick(ray) : null;
      this.showRay(ray, hit !== null);
      if (hit?.target === "gear") overGear = true;
      if (hit?.target === "panel") hovering = hit.pixel;
      if (hit?.target === "auto") autoHovering = hit.pixel;
      const squeezed = squeeze >= TRIGGER_RELEASED;
      if (!squeezed) this.triggerOwner[side] = null;
      else if (this.triggerOwner[side] === null) this.triggerOwner[side] = hit ? "menu" : "arm";
      // A trigger pull that lands on the menu is a click, and must not also be a command to
      // the gripper. This rule came from the original page and is why the menu is usable at
      // all while something is held.
      if (this.triggerOwner[side] === "menu" && hands[side]) hands[side]!.trigger = 0;
      const trigger = pulled && this.triggerOwner[side] === "menu";
      this.pointer(hit, trigger, this.lastTrigger[side], source);
      this.lastTrigger[side] = trigger;
    }

    this.cursor = hovering;
    this.autoCursor = autoHovering;
    this.showGearHover(overGear);
    this.menuButton(hands, raw);
    const now = performance.now();
    if (now - this.widgetsChangedAt > 100) {   // ten redraws a second is plenty for a menu
      this.widgetsChangedAt = now;
      this.layoutMenu();
    }

    if (Object.keys(hands).length && now - this.lastSent >= 1000 / HAND_HZ) {
      this.lastSent = now;
      this.cb.onHands(hands);
    }
  }

  /**
   * X on the free hand opens and shuts the menu -- but only as a tap.
   *
   * That button also rolls the gripper, and roll is something you hold: the two can share it
   * as long as a short press never reaches the arm and a long one is nothing but roll. So X is
   * held back for TAP_MS; let go inside that and it was a tap, and the menu moves. Keep
   * holding and it is passed through from then on, and the arm rolls as it always did, having
   * lost only the first fifth of a second.
   */
  private menuButton(hands: Partial<Record<"left" | "right", HandFrame>>,
                     raw: Partial<Record<"left" | "right", boolean>>): void {
    const free = this.cb.view().settings.hand === "left" ? "right" : "left";
    const hand = hands[free];
    if (!hand || raw[free] === undefined) { this.menuButtonSince = 0; return; }
    const now = performance.now();
    const down = raw[free]!;
    if (down && this.menuButtonSince === 0) this.menuButtonSince = now;
    const held = this.menuButtonSince > 0 ? now - this.menuButtonSince : 0;
    if (down && held < TAP_MS) hand.a = false;          // still might be a tap: the arm waits
    if (!down && this.menuButtonSince > 0) {
      if (held < TAP_MS) this.setOpen(!this.open);      // a tap, and nothing reached the arm
      this.menuButtonSince = 0;
    }
  }

  /** Light the gear up while it is being pointed at, so it reads as something to press. */
  private showGearHover(over: boolean): void {
    const material = this.gearMesh.material as MeshBasicMaterial;
    material.color.set(over ? 0x8fc0ff : 0xffffff);
    this.gearMesh.scale.setScalar(over ? 1.18 : 1);
  }

  /** Draw the line the controller is casting, and colour it when it lands on the menu. */
  private showRay(ray: { origin: DOMPointReadOnly; direction: [number, number, number] } | null,
                  onMenu: boolean): void {
    if (!ray) {
      this.rayLine.visible = false;
      return;
    }
    const direction = new Vector3(...ray.direction).normalize();
    this.rayLine.position.set(ray.origin.x, ray.origin.y, ray.origin.z);
    this.rayLine.lookAt(this.rayLine.position.clone().add(direction));
    this.rayLine.scale.setScalar(onMenu ? 1.6 : 2.5);
    (this.rayLine.material as LineBasicMaterial).color.set(onMenu ? 0x5b9dff : 0x8c98a6);
    this.rayLine.visible = true;
  }

  private pick(ray: { origin: DOMPointReadOnly; direction: [number, number, number] }):
    { target: "panel" | "gear" | "auto"; pixel: { x: number; y: number } } | null {
    // The menu hangs off the screen group, which was moved this tick; three only refreshes
    // world matrices during render, so without this the first aim after a re-centre is thrown
    // at where the panel used to be.
    this.screen.group.updateMatrixWorld(true);
    this.raycaster.set(new Vector3(ray.origin.x, ray.origin.y, ray.origin.z),
                       new Vector3(...ray.direction).normalize());
    // Only ever what is on screen. The raycaster is happy to hit a hidden mesh, and a hidden
    // gear under the panel is how closing the menu came to press whatever was behind it.
    const targets = this.open ? [this.panelMesh] : this.automation ? [this.gearMesh, this.autoMesh] : [this.gearMesh];
    for (const intersection of this.raycaster.intersectObjects(targets, false)) {
      const uv = intersection.uv as Vector2 | undefined;
      if (!uv) continue;
      if (intersection.object === this.gearMesh) return { target: "gear", pixel: { x: 0, y: 0 } };
      if (intersection.object === this.autoMesh) {
        return { target: "auto", pixel: { x: uv.x * PANEL_W, y: (1 - uv.y) * this.autoPanel.height } };
      }
      return { target: "panel", pixel: { x: uv.x * PANEL_W, y: (1 - uv.y) * this.panel.height } };
    }
    return null;
  }

  private pointer(hit: { target: "panel" | "gear" | "auto"; pixel: { x: number; y: number } } | null,
                  down: boolean, wasDown: boolean, source: XRInputSource): void {
    if (this.dragging) {
      const slider = this.panel.sliderById(this.dragging);
      if (slider && hit?.target === "panel") {
        const fraction = Math.max(0, Math.min(1, (hit.pixel.x - 22) / (PANEL_W - 44)));
        this.applySlider(slider, valueFromFraction(slider, fraction), false);
      }
      if (!down) {
        const held = slider;
        if (held) this.applySlider(held, held.value, true);
        this.dragging = null;
      }
      return;
    }
    if (!down || wasDown || !hit) return;
    if (hit.target === "gear") {
      this.setOpen(!this.open);
      buzz(source, 0.35, 40);
      return;
    }
    const found = (hit.target === "auto" ? this.autoPanel : this.panel).hit(hit.pixel.x, hit.pixel.y);
    if (!found) return;
    buzz(source, 0.4, 45);
    const widget = found.widget as Widget;
    if (widget.kind === "button") this.fire(widget.id);
    else if (widget.kind === "choice" && "option" in found) this.choose(widget.id, found.option);
    else if (widget.kind === "slider" && "fraction" in found) {
      this.dragging = widget.id;
      this.applySlider(widget, valueFromFraction(widget, found.fraction), false);
    }
  }

  private applySlider(slider: { id: string; value: number }, value: number, final: boolean): void {
    slider.value = value;   // so the panel follows the finger before the robot answers
    if (slider.id === "convergence" || slider.id === "verticalOffset") {
      this.screen.set({ [slider.id]: value });
      if (final) this.cb.onLocal({ [slider.id]: value });
      return;
    }
    this.cb.onSetting(slider.id as keyof Settings, value as never, final);
  }

  private choose(id: string, option: string): void {
    if (id === "swap") {
      const swapEyes = option === "yes";
      this.screen.set({ swapEyes });
      this.cb.onLocal({ swapEyes });
      return;
    }
    if (id === "overlay") {
      this.cb.onSetting("overlay" as keyof Settings, (option === "on") as never, true);
      return;
    }
    if (id === "showMap") {
      this.cb.onLocal({ showMap: option === "on" });
      return;
    }
    if (id === "flatten") {
      const flatten = option === "flat";
      this.screen.set({ flatten });
      this.cb.onLocal({ flatten });
      return;
    }
    this.cb.onSetting(id as keyof Settings, option as never, true);
  }

  private fire(id: string): void {
    if (id === "close_menu") { this.setOpen(false); return; }
    if (id === "open_automation") { this.setOpen(false); this.setAutomation(true); return; }
    if (id === "close_automation") { this.setAutomation(false); return; }
    if (id === "clear_path" && performance.now() > this.clearArmedUntil) {
      // It throws the taught path away (the arm keeps one copy, pick_loop_config.json.bak): a
      // trigger pull that meant the button beside it should not be enough.
      this.clearArmedUntil = performance.now() + CLEAR_CONFIRM_MS;
      this.widgetsChangedAt = 0;   // say "press again" now, not on the next redraw
      return;
    }
    if (id === "clear_path") this.clearArmedUntil = 0;
    if (id === "recentre") { this.recentre(); return; }
    if (id === "reset_stereo") {
      this.screen.set({ convergence: 0, verticalOffset: 0 });
      this.cb.onLocal({ convergence: 0, verticalOffset: 0 });
      return;
    }
    this.cb.onAction(id);
  }
}

const MAP_SCALE = 1.4;                   // the box map, 35 cm of box = 49 cm in front of you
const VIEWER = new Vector3(0, 0, 1);     // where you are, in the screen's frame: one screen-distance in front
const MAP_HOME = new Vector3(-(1.5 / 2 + 0.35), -0.28, 0.35);   // the stand, left of the picture
const MAP_MARGIN = (3 * Math.PI) / 180;  // clear of the picture's edge by this much
const MAP_TURN_MAX = (35 * Math.PI) / 180;   // and no further round than your left shoulder
const UP = new Vector3(0, 1, 0);
const CORNER = new Vector3();
const MAP_M = new Matrix4();
const AUTO_WORLD_W = 0.46;               // metres: the automation panel, ...
const AUTO_DISTANCE = 0.9;               // ... this far from you,
const AUTO_BEARING = (50 * Math.PI) / 180;   // this far right of straight ahead (the picture's edge is at 37),
const AUTO_TOP = -0.1;                   // its top edge this far under eye level, below the gear
const CLEAR_CONFIRM_MS = 3000;
const TRIGGER_RELEASED = 0.15;           // under this a trigger is let go, and its next press is a new one
const RIM_W = 1.1;                       // metres, on a 1.5 m screen
const RIM_PX_W = 1100, RIM_PX_H = 150;

/** The rim warning: a headline and the arm's own advice, red for a hit, amber for close. */
function drawRim(canvas: HTMLCanvasElement, hit: boolean, head: string, say: string): void {
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = hit ? "rgba(150, 18, 18, 0.88)" : "rgba(130, 84, 8, 0.85)";
  ctx.beginPath();
  ctx.roundRect(4, 4, canvas.width - 8, canvas.height - 8, 18);
  ctx.fill();
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = "bold 50px system-ui, sans-serif";
  ctx.fillText(`\u26A0 ${head}`, canvas.width / 2, 48, canvas.width - 40);
  ctx.font = "34px system-ui, sans-serif";
  ctx.fillText(say, canvas.width / 2, 110, canvas.width - 40);
}

/** Lay a panel's widgets out, draw them and fit its quad to them. Returns the quad's height, metres. */
function layOut(panel: Panel, texture: CanvasTexture, mesh: Mesh, worldW: number, widgets: Widget[],
                cursor: { x: number; y: number } | null, dragging: string | null): number {
  const canvasH = panel.canvas.height;
  const height = panel.layout(widgets);
  // three sizes a texture's GPU storage once, on its first upload, and every later upload
  // of a bigger canvas is refused -- so a menu that grew a row (Motion adds "Dominant arm")
  // went on showing the old picture while the clicks landed on the new layout. Dropping the
  // storage makes the next upload allocate it again at the new size.
  if (panel.canvas.height !== canvasH) texture.dispose();
  // Only the part of the canvas the panel uses goes on the quad, so a pixel on the quad is
  // the same pixel in the layout that `pick` reads it back through.
  texture.repeat.set(1, height / panel.canvas.height);
  texture.offset.set(0, 1 - height / panel.canvas.height);
  panel.draw(cursor, dragging);
  texture.needsUpdate = true;
  const worldH = worldW * (height / PANEL_W);
  const geometry = mesh.geometry as PlaneGeometry;
  if (Math.abs(geometry.parameters.height - worldH) > 1e-3) {
    geometry.dispose();
    mesh.geometry = new PlaneGeometry(worldW, worldH);
  }
  mesh.geometry.computeBoundingBox();
  return worldH;
}

/** A gear, drawn once. */
function gearCanvas(): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = 128;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "rgba(12, 15, 19, 0.92)";
  ctx.beginPath();
  ctx.arc(64, 64, 62, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#5b9dff";
  ctx.lineWidth = 6;
  ctx.stroke();
  ctx.fillStyle = "#e8ecf1";
  for (let i = 0; i < 8; i += 1) {
    ctx.save();
    ctx.translate(64, 64);
    ctx.rotate((i * Math.PI) / 4);
    ctx.fillRect(-7, -46, 14, 18);
    ctx.restore();
  }
  ctx.beginPath();
  ctx.arc(64, 64, 26, 0, Math.PI * 2);
  ctx.lineWidth = 12;
  ctx.strokeStyle = "#e8ecf1";
  ctx.stroke();
  return canvas;
}

/** Tell the page it is visible for as long as the headset owns the display. */
function forceVisible(): () => void {
  const target = Document.prototype;
  const original = Object.getOwnPropertyDescriptor(target, "visibilityState");
  try {
    Object.defineProperty(document, "visibilityState", { get: () => "visible", configurable: true });
    Object.defineProperty(document, "hidden", { get: () => false, configurable: true });
  } catch {
    return () => undefined;
  }
  return () => {
    try {
      delete (document as unknown as Record<string, unknown>).visibilityState;
      delete (document as unknown as Record<string, unknown>).hidden;
      if (original) Object.defineProperty(target, "visibilityState", original);
    } catch {
      // the override outlives the session; harmless on a page that is about to be left
    }
  };
}
